import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { supabase } from '../../../../lib/supabase';
import { createLeaseApplication } from '../../../../services/supabase/lease-applications';

interface AddApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preventCloseOnOutsideClick?: boolean;
}

export default function AddApplicationModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  preventCloseOnOutsideClick = true
}: AddApplicationModalProps) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose, preventCloseOnOutsideClick);

  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    app_lot: '',
    app_type: '',
    app_date: new Date().toISOString().split('T')[0],
    app_desc: '',
    app_permit: '',
    app_doc: '',
    app_approv_date: '',
    app_const_appr_date: '',
    app_appr_doc: '',
    app_supp_docs: '',
    app_dev_comp: '',
    app_final: 'N',
    app_sublease: '',
    app_appl: 0, // Default value
    app_status: '', // Default value
    app_note: '' // Default value
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Fetch available lots
  const { data: lots } = useQuery({
    queryKey: ['lots'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('land_lots')
        .select('lot_id, lot_community')
        .order('lot_id');
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch application types
  const { data: applicationTypes } = useQuery({
    queryKey: ['application-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('application_types')
        .select('app_type_id, app_desc')
        .order('app_desc');
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch subleases
  const { data: subleases } = useQuery({
    queryKey: ['subleases'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subleases')
        .select('lease_id')
        .order('lease_id');
      
      if (error) throw error;
      return data;
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await createLeaseApplication({
        app_lot: formData.app_lot,
        app_type: formData.app_type,
        app_date: formData.app_date,
        app_desc: formData.app_desc || null,
        app_permit: formData.app_permit || null,
        app_doc: formData.app_doc || null,
        app_approv_date: formData.app_approv_date || null,
        app_const_appr_date: formData.app_const_appr_date || null,
        app_appr_doc: formData.app_appr_doc || null,
        app_supp_docs: formData.app_supp_docs || null,
        app_dev_comp: formData.app_dev_comp || null,
        app_final: formData.app_final,
        app_sublease: formData.app_sublease ? parseInt(formData.app_sublease) : null,
        app_appl: formData.app_appl,
        app_status: formData.app_status,
        app_note: formData.app_note
      });

      queryClient.invalidateQueries({ queryKey: ['admin', 'applications'] });
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating application:', err);
      setError(err instanceof Error ? err.message : 'Failed to create application');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-0 z-[100] overflow-y-auto"
    >
      <div 
        ref={modalContentRef}
        className="relative bg-white w-full md:max-w-2xl md:rounded-lg md:my-8 min-h-screen md:min-h-0 max-h-full md:max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 md:p-6 border-b bg-white">
          <h3 className="text-xl font-semibold text-gray-900">Add New Application</h3>
          <button 
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content - with scrollable area */}
        <form id="add-application-form" onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Lot *
                </label>
                <select
                  value={formData.app_lot}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_lot: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  required
                >
                  <option value="">Select a lot</option>
                  {lots?.map((lot) => (
                    <option key={lot.lot_id} value={lot.lot_id}>
                      {lot.lot_id} - {lot.lot_community}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Application Type *
                </label>
                <select
                  value={formData.app_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_type: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  required
                >
                  <option value="">Select application type</option>
                  {applicationTypes?.map((type) => (
                    <option key={type.app_type_id} value={type.app_type_id}>
                      {type.app_desc}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Application Date *
                </label>
                <input
                  type="date"
                  value={formData.app_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_date: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Sublease
                </label>
                <select
                  value={formData.app_sublease}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_sublease: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                >
                  <option value="">No sublease (new application)</option>
                  {subleases?.map((sublease) => (
                    <option key={sublease.lease_id} value={sublease.lease_id}>
                      {sublease.lease_id}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Application Details */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Application Details</h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Description
                </label>
                <textarea
                  value={formData.app_desc}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_desc: e.target.value }))}
                  rows={3}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  placeholder="Enter application description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Permitted Use
                </label>
                <input
                  type="text"
                  value={formData.app_permit}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_permit: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  placeholder="Enter permitted use"
                />
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Documents</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Application Document
                </label>
                <input
                  type="text"
                  value={formData.app_doc}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_doc: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  placeholder="Enter document reference or URL"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Approval Document
                </label>
                <input
                  type="text"
                  value={formData.app_appr_doc}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_appr_doc: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  placeholder="Enter approval document reference"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-500">
                  Supporting Documents
                </label>
                <input
                  type="text"
                  value={formData.app_supp_docs}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_supp_docs: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  placeholder="Enter supporting documents references"
                />
              </div>
            </div>
          </div>

          {/* Approval Information */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Approval Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Approval Date
                </label>
                <input
                  type="date"
                  value={formData.app_approv_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_approv_date: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Construction Approval Date
                </label>
                <input
                  type="date"
                  value={formData.app_const_appr_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_const_appr_date: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Development Completion Date
                </label>
                <input
                  type="date"
                  value={formData.app_dev_comp}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_dev_comp: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Final Status
                </label>
                <select
                  value={formData.app_final}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_final: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                >
                  <option value="N">Pending</option>
                  <option value="Y">Completed</option>
                </select>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 p-4 md:p-6 border-t bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-application-form"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md disabled:opacity-50"
          >
            {isSubmitting ? 'Creating...' : 'Create Application'}
          </button>
        </div>
      </div>
    </div>
  );
}
